function queryShadowDom(selector, root = document) {
    const el = root.querySelector(selector);
    if (el) return el;

    const hosts = root.querySelectorAll('*');

    for (const host of hosts) {
        if (host.shadowRoot) {
            const shadowEl = queryShadowDom(selector, host.shadowRoot);
            if (shadowEl) return shadowEl;
        }
    }

    return null;
}

function queryShadowDomAll(selector, root = document) {
    const results = root.querySelectorAll ? Array.from(root.querySelectorAll(selector)) : [];
    const hosts = root.querySelectorAll ? root.querySelectorAll('*') : [];

    for (const host of hosts) {
        if (host.shadowRoot) {
            results.push(...queryShadowDomAll(selector, host.shadowRoot));
        }
    }

    return results;
}

/**
 * Recharge subscription copy replacement + Webflow controls
 *
 * Webflow can control:
 * 1. Copy A / B with:
 *    data-recharge-copy-variant="a"
 *    data-recharge-copy-variant="b"
 *
 * 2. Hide/show all custom Recharge copy with:
 *    class="hide-recharge-subscribe-copy"
 *    or data-hide-recharge-subscribe-copy="true"
 */

const RECHARGE_OLD_COPY_REGEX = /Subscribe:\s*ships free\s*\+\s*gift\s*\(\$20 value\)/i;

const RECHARGE_NEW_COPY_HTML = `
  <span class="recharge-subscribe-copy">
    <span class="recharge-subscribe-copy--earn">
      Subscribe &amp; earn&nbsp;<span class="points_earned">0</span>&nbsp;points
    </span>

    <span class="recharge-subscribe-copy--value">
      Subscribe: ships free + gift ($20 value)
    </span>
  </span>
`;

const RECHARGE_HIDE_COPY_CLASS = 'hide-recharge-subscribe-copy';
const RECHARGE_HIDE_COPY_ATTR = 'data-hide-recharge-subscribe-copy';
const RECHARGE_COPY_VARIANT_ATTR = 'data-recharge-copy-variant';

let rechargeCopyRaf = null;
let rechargeCopyRetrying = false;

function shouldHideRechargeSubscribeCopy() {
    const html = document.documentElement;
    const body = document.body;

    return (
        html.classList.contains(RECHARGE_HIDE_COPY_CLASS) ||
        body?.classList.contains(RECHARGE_HIDE_COPY_CLASS) ||
        html.getAttribute(RECHARGE_HIDE_COPY_ATTR) === 'true' ||
        body?.getAttribute(RECHARGE_HIDE_COPY_ATTR) === 'true' ||
        !!document.querySelector(`[${RECHARGE_HIDE_COPY_ATTR}="true"]`)
    );
}

function getRechargeCopyVariant() {
    const htmlVariant = document.documentElement.getAttribute(RECHARGE_COPY_VARIANT_ATTR);
    const bodyVariant = document.body?.getAttribute(RECHARGE_COPY_VARIANT_ATTR);
    const markerVariant = document
        .querySelector(`[${RECHARGE_COPY_VARIANT_ATTR}]`)
        ?.getAttribute(RECHARGE_COPY_VARIANT_ATTR);

    const variant = htmlVariant || bodyVariant || markerVariant || 'a';

    return variant.toLowerCase() === 'b' ? 'b' : 'a';
}

function syncRechargeSubscribeCopyVariant(root = document) {
    const variant = getRechargeCopyVariant();
    const copyEls = queryShadowDomAll('.recharge-subscribe-copy', root);

    copyEls.forEach((copyEl) => {
        const earnCopy = copyEl.querySelector('.recharge-subscribe-copy--earn');
        const valueCopy = copyEl.querySelector('.recharge-subscribe-copy--value');

        if (!earnCopy || !valueCopy) return;

        if (variant === 'b') {
            earnCopy.style.setProperty('display', 'none', 'important');
            valueCopy.style.removeProperty('display');
        } else {
            earnCopy.style.removeProperty('display');
            valueCopy.style.setProperty('display', 'none', 'important');
        }
    });
}

function syncRechargeSubscribeCopyVisibility(root = document) {
    const shouldHide = shouldHideRechargeSubscribeCopy();
    const copyEls = queryShadowDomAll('.recharge-subscribe-copy', root);

    copyEls.forEach((el) => {
        if (shouldHide) {
            el.style.setProperty('display', 'none', 'important');
        } else {
            el.style.removeProperty('display');
        }
    });

    syncRechargeSubscribeCopyVariant(root);
}

function observeWebflowRechargeCopyToggle() {
    const targets = [document.documentElement, document.body].filter(Boolean);

    targets.forEach((target) => {
        if (target.__rechargeCopyToggleObserverAdded) return;

        const observer = new MutationObserver(() => {
            scheduleRechargeCopyReplacement();
            syncRechargeSubscribeCopyVisibility();
        });

        observer.observe(target, {
            attributes: true,
            attributeFilter: [
                'class',
                RECHARGE_HIDE_COPY_ATTR,
                RECHARGE_COPY_VARIANT_ATTR
            ]
        });

        target.__rechargeCopyToggleObserverAdded = true;
        target.__rechargeCopyToggleObserver = observer;
    });

    if (document.body && !document.body.dataset.rechargeCopyMarkerObserverAdded) {
        const markerObserver = new MutationObserver(() => {
            syncRechargeSubscribeCopyVisibility();
        });

        markerObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: [
                RECHARGE_HIDE_COPY_ATTR,
                RECHARGE_COPY_VARIANT_ATTR
            ]
        });

        document.body.dataset.rechargeCopyMarkerObserverAdded = 'true';
        document.body.__rechargeCopyMarkerObserver = markerObserver;
    }
}

function isInsideInjectedRechargeCopy(textNode) {
    const parentEl = textNode.parentElement;

    return !!parentEl?.closest?.('.recharge-subscribe-copy');
}

function replaceTextInRoot(root) {
    if (!root) return false;

    let replaced = false;

    const walker = document.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode(node) {
                if (isInsideInjectedRechargeCopy(node)) {
                    return NodeFilter.FILTER_REJECT;
                }

                return RECHARGE_OLD_COPY_REGEX.test(node.nodeValue || '')
                    ? NodeFilter.FILTER_ACCEPT
                    : NodeFilter.FILTER_REJECT;
            }
        }
    );

    const nodesToReplace = [];
    let node;

    while ((node = walker.nextNode())) {
        nodesToReplace.push(node);
    }

    nodesToReplace.forEach((textNode) => {
        const wrapper = document.createElement('span');
        wrapper.innerHTML = RECHARGE_NEW_COPY_HTML.trim();

        if (wrapper.firstElementChild && textNode.parentNode) {
            textNode.parentNode.replaceChild(wrapper.firstElementChild, textNode);
            replaced = true;
        }
    });

    return replaced;
}

function replaceRechargeSubscriptionCopy(root = document) {
    let replaced = replaceTextInRoot(root);

    const hosts = root.querySelectorAll ? root.querySelectorAll('*') : [];

    for (const host of hosts) {
        if (host.shadowRoot) {
            replaced = replaceRechargeSubscriptionCopy(host.shadowRoot) || replaced;
        }
    }

    syncRechargeSubscribeCopyVisibility(root);

    return replaced;
}

function scheduleRechargeCopyReplacement() {
    if (rechargeCopyRaf) return;

    rechargeCopyRaf = requestAnimationFrame(() => {
        rechargeCopyRaf = null;

        replaceRechargeSubscriptionCopy();
        updatePointsEarned();
        syncRechargeSubscribeCopyVisibility();
        observeRechargeShadowRoots();
    });
}

function observeRechargeShadowRoots() {
    const rechargeRoots = queryShadowDomAll('recharge-subscription-widget, rc-selling-plans');

    rechargeRoots.forEach((host) => {
        const shadowRoot = host.shadowRoot;

        if (!shadowRoot || shadowRoot.__rechargeCopyObserverAdded) return;

        const observer = new MutationObserver(() => {
            scheduleRechargeCopyReplacement();
        });

        observer.observe(shadowRoot, {
            childList: true,
            subtree: true,
            characterData: true
        });

        shadowRoot.__rechargeCopyObserverAdded = true;
        shadowRoot.__rechargeCopyObserver = observer;
    });
}

function observeRechargeCopyChanges() {
    if (document.body && !document.body.dataset.rechargeCopyObserverAdded) {
        const observer = new MutationObserver(() => {
            scheduleRechargeCopyReplacement();
            observeRechargeShadowRoots();
            syncRechargeSubscribeCopyVisibility();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        document.body.dataset.rechargeCopyObserverAdded = 'true';
        document.body.__rechargeCopyObserver = observer;
    }

    observeRechargeShadowRoots();
}

function tryReplaceRechargeSubscriptionCopy(attemptsLeft = 120) {
    if (rechargeCopyRetrying && attemptsLeft === 120) return;

    if (attemptsLeft === 120) {
        rechargeCopyRetrying = true;
    }

    replaceRechargeSubscriptionCopy();
    updatePointsEarned();
    syncRechargeSubscribeCopyVisibility();
    observeRechargeCopyChanges();
    observeWebflowRechargeCopyToggle();

    if (attemptsLeft > 0) {
        requestAnimationFrame(() => {
            tryReplaceRechargeSubscriptionCopy(attemptsLeft - 1);
        });
    } else {
        rechargeCopyRetrying = false;
    }
}

function updatePointsEarned() {
    const priceEl = queryShadowDom('.rc-price');
    const pointsEls = queryShadowDomAll('.points_earned');

    if (priceEl && pointsEls.length) {
        const numeric_price = parseFloat(priceEl.textContent.replace('$', '').trim());

        if (!isNaN(numeric_price)) {
            const pointsValue = String(Math.round(numeric_price * 2));

            pointsEls.forEach((pointsEl) => {
                if (pointsEl.textContent !== pointsValue) {
                    pointsEl.textContent = pointsValue;
                }
            });
        }
    }
}

function observePriceChanges() {
    const priceEl = queryShadowDom('.rc-price');

    if (priceEl && !priceEl.dataset.priceObserverAdded) {
        const observer = new MutationObserver(() => {
            updatePointsEarned();
        });

        observer.observe(priceEl, {
            childList: true,
            characterData: true,
            subtree: true
        });

        priceEl.dataset.priceObserverAdded = 'true';
    }
}

function ensureRechargeStyles(shadowRoot) {
    if (!shadowRoot.querySelector('#recharge-features-style')) {
        const style = document.createElement('style');
        style.id = 'recharge-features-style';
        style.textContent = `
      ul.recharge__features {
        margin-top: 12px;
        margin-bottom: 0px;
        gap: 4px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        padding-left: 0;
        list-style: none;
      }

      ul.recharge__features li {
        font-size: 14px;
        color: #5b6670;
        font-weight: 300;
        line-height: 1.4;
        margin: 0;
        padding: 6px 0;
        display: flex;
        align-items: center;
      }

      ul.recharge__features li::before {
        content: "\\2713";
        font-weight: 600;
        font-size: 13px;
        color: #5B6670;
        display: inline-block;
        width: 15px;
      }

      .recharge-subscribe-copy {
        display: inline;
      }

      .recharge-subscribe-copy--earn,
      .recharge-subscribe-copy--value {
        display: inline;
      }

      @media (min-width: 532px) and (max-width: 749px),
             (min-width: 1270px),
             (min-width: 1440px) {
        ul.recharge__features {
          grid-template-columns: 1fr 1fr 1fr;
        }
      }

      @media (min-width: 750px) and (max-width: 1439px) {
        ul.recharge__features {
          grid-template-columns: 1fr 1fr !important;
        }
      }
    `;

        shadowRoot.appendChild(style);
    }
}

function injectFeaturesOnSubscription() {
    const recharge_selling_plans = queryShadowDom('rc-selling-plans');
    const shadowRoot = recharge_selling_plans?.shadowRoot;

    if (!shadowRoot) return;

    ensureRechargeStyles(shadowRoot);
    tryReplaceRechargeSubscriptionCopy();

    let features = shadowRoot.querySelector('.recharge__features');

    if (!features) {
        const ul = document.createElement('ul');
        ul.classList.add('recharge__features');
        ul.innerHTML = `
      <li class='add-rc__features'>free gift</li>
      <li class='add-rc__features'>earn&nbsp;<span class="points_earned">0</span>&nbsp;points</li>
      <li>free shipping</li>
      <li>free 30-day returns</li>
      <li>easy cancellations</li>
      <li class='add-rc__features'>exclusive offers</li>
    `;

        shadowRoot.appendChild(ul);
    } else {
        features.style.display = "";
    }

    updatePointsEarned();
    observePriceChanges();
    syncRechargeSubscribeCopyVisibility();
}

function injectInfoIcon() {
    const selector = 'div.rc-purchase-option__selector[part*="rc-purchase-option__selector_subscription"]';
    const recharge_info = queryShadowDom(selector);

    if (recharge_info && !recharge_info.querySelector('.recharge__icon')) {
        const svgString = `
      <svg class="recharge__svg" xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
        <g clip-path="url(#clip0_2_5080)">
          <mask id="mask0_2_5080" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="17" height="17">
            <path d="M16.1913 0.478638H0.191345V16.4786H16.1913V0.478638Z" fill="white"/>
          </mask>
          <g mask="url(#mask0_2_5080)">
            <path d="M8.19133 11.1452V8.47856M8.19133 5.81189H8.198M14.858 8.47856C14.858 12.1605 11.8732 15.1452 8.19133 15.1452C4.50943 15.1452 1.52466 12.1605 1.52466 8.47856C1.52466 4.79666 4.50943 1.81189 8.19133 1.81189C11.8732 1.81189 14.858 4.79666 14.858 8.47856Z" stroke="#5B6670" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
          </g>
        </g>
        <defs>
          <clipPath id="clip0_2_5080">
            <rect width="16" height="16" fill="white" transform="translate(0.191345 0.478638)"/>
          </clipPath>
        </defs>
      </svg>
    `;

        recharge_info.insertAdjacentHTML(
            'beforeend',
            '<span class="recharge__icon">' + svgString + '</span>'
        );

        const rcInfoIcon = recharge_info.querySelector('.recharge__icon');

        if (rcInfoIcon) {
            rcInfoIcon.addEventListener('click', () => {
                rechargeSubscriptionDetails(rcInfoIcon);
            });
        }
    }
}

function setDisableRechargeVariantValue(isDisabled) {
    const disableRechargeVariantInput = document.querySelector(
        '.product-form input[name="properties[_disableRechargeVariant]"]'
    );

    if (disableRechargeVariantInput) {
        disableRechargeVariantInput.value = isDisabled ? "true" : "false";
    }
}

function syncDisableRechargeVariantOnLoad() {
    const hasRechargeWidget = !!document
        .querySelector('recharge-subscription-widget')
        ?.shadowRoot?.querySelector('.rc-widget__root');

    setDisableRechargeVariantValue(!hasRechargeWidget);
}

function isTryNowActive() {
    const tryNowInput = document.querySelector('input[name="properties[_tryNow]"]');
    return tryNowInput?.value === 'true';
}

function setRechargeOrderValue(isRechargeOrder) {
    const rechargeOrderInput = document.querySelector(
        '.product-form input[name="properties[_rechargeOrder]"]'
    );

    if (!rechargeOrderInput) return;

    if (isTryNowActive()) {
        rechargeOrderInput.value = 'false';
        return;
    }

    rechargeOrderInput.value = isRechargeOrder ? 'true' : 'false';
}

function addSubscriptionDetails() {
    const kicker_headings = document.querySelectorAll("span.os-kicker-heading");
    const installment_headings = document.querySelectorAll("div.klarna-afterpay-installment");
    const os_bodys = document.querySelectorAll("div.os-body");
    const os_headings = document.querySelectorAll("div.os-heading");
    const payment_badges = document.querySelectorAll("span.payment_badges");
    const recharge_badge = document.querySelectorAll("span.recharge-icon");
    const powerBy_section = document.querySelectorAll("span.os-poweredBy");

    recharge_badge.forEach(function(rc_badge) {
        rc_badge.style.display = "block";
    });

    installment_headings.forEach(function(installment_heading) {
        installment_heading.style.display = "none";
    });

    payment_badges.forEach(function(payment_badge) {
        payment_badge.style.display = "none";
    });

    kicker_headings.forEach(function(kicker_heading) {
        kicker_heading.textContent = "Dermalogica Subscription";
    });

    os_headings.forEach(function(os_heading) {
        os_heading.textContent = "How subscriptions work";
    });

    os_bodys.forEach(function(os_body) {
        os_body.innerHTML = "<p>Products are automatically delivered and charged on your schedule. No obligation, modify or cancel your subscription within 24 hours of the next scheduled shipment date.</p><a href='https://www.dermalogica.com/pages/subscription'><span style='color:#5E93DB'>Learn more</span></a>";
    });

    powerBy_section.forEach(function(powerBy) {
        powerBy.style.display = "flex";
    });
}

function rechargeSubscriptionDetails(rcInfoIcon) {
    const onSiteContainer = document.getElementById("os-modal-desktop");
    const closeIcon = document.getElementById("os-close");
    const closeIconMobile = document.getElementById("os-close-mobile");

    if (!rcInfoIcon) {
        return;
    }

    // Mobile layout
    if (window.innerWidth < 600) {
        const osMobile = document.getElementById("os-modal-mobile");

        if (!osMobile) return;

        osMobile.style.display = "block";
        addSubscriptionDetails();

        if (closeIconMobile && !closeIconMobile.dataset.listenerAdded) {
            closeIconMobile.addEventListener("click", function() {
                osMobile.style.display = "none";
            });

            closeIconMobile.dataset.listenerAdded = "true";
        }

        return;
    }

    // Desktop layout
    if (!onSiteContainer) return;

    if (!onSiteContainer.classList.contains("recharge")) {
        setTimeout(function() {
            onSiteContainer.classList.add("show");
            onSiteContainer.classList.add("recharge");
        }, 500);
    }

    const iconRect = rcInfoIcon.getBoundingClientRect();
    const iconTop = iconRect.top + window.scrollY;
    const iconLeft = iconRect.left + window.scrollX;

    onSiteContainer.style.position = "absolute";
    onSiteContainer.style.top = (iconTop - 200) + "px";
    onSiteContainer.style.left = (iconLeft - 350) + "px";
    onSiteContainer.style.display = "block";

    addSubscriptionDetails();

    if (closeIcon && !closeIcon.dataset.listenerAdded) {
        closeIcon.addEventListener("click", function() {
            onSiteContainer.style.display = "none";
            onSiteContainer.classList.remove("show");
            onSiteContainer.classList.remove("recharge");
        });

        closeIcon.dataset.listenerAdded = "true";
    }

    if (!document.body.dataset.rechargeOutsideListenerAdded) {
        document.addEventListener("click", function(event) {
            if (onSiteContainer.classList.contains("recharge")) {
                const clickedInsideModal = onSiteContainer.contains(event.target);
                const currentIcon = queryShadowDom('.recharge__icon');
                const clickedInfoIcon = currentIcon && (currentIcon.contains(event.target) || event.target === currentIcon);

                if (!clickedInsideModal && !clickedInfoIcon) {
                    onSiteContainer.style.display = "none";
                    onSiteContainer.classList.remove("show");
                    onSiteContainer.classList.remove("recharge");
                }
            }
        });

        document.body.dataset.rechargeOutsideListenerAdded = "true";
    }
}

function handlePurchaseOptionChange(purchaseType) {
    tryReplaceRechargeSubscriptionCopy();

    if (purchaseType === 'subscription') {
        setRechargeOrderValue(true);
        setDisableRechargeVariantValue(false);

        function tryInject(attemptsLeft) {
            const el = queryShadowDom('rc-selling-plans');

            if (el?.shadowRoot) {
                injectFeaturesOnSubscription();
                injectInfoIcon();
                tryReplaceRechargeSubscriptionCopy();
                syncRechargeSubscribeCopyVisibility();
            } else if (attemptsLeft > 0) {
                requestAnimationFrame(() => tryInject(attemptsLeft - 1));
            }
        }

        tryInject(60);
    } else {
        setRechargeOrderValue(false);
        setDisableRechargeVariantValue(false);
        tryReplaceRechargeSubscriptionCopy();
        syncRechargeSubscribeCopyVisibility();

        const recharge_selling_plans = queryShadowDom('rc-selling-plans');
        const features = recharge_selling_plans?.shadowRoot?.querySelector('.recharge__features');

        if (features) features.style.display = 'none';
    }
}

function initWithRechargeApi(api) {
    syncDisableRechargeVariantOnLoad();

    tryReplaceRechargeSubscriptionCopy();
    observeRechargeCopyChanges();
    observeWebflowRechargeCopyToggle();
    syncRechargeSubscribeCopyVisibility();

    // Set initial state — API events only fire on change, not on load
    const subscriptionRadio = queryShadowDom('input[type="radio"][value="subscription"]');
    handlePurchaseOptionChange(subscriptionRadio?.checked ? 'subscription' : 'onetime');

    api.events.onPurchaseOptionChange((purchaseType) => {
        tryReplaceRechargeSubscriptionCopy();
        handlePurchaseOptionChange(purchaseType);
        syncRechargeSubscribeCopyVisibility();
    });

    api.events.onSellingPlanChange(() => {
        updatePointsEarned();
        observePriceChanges();
        tryReplaceRechargeSubscriptionCopy();
        syncRechargeSubscribeCopyVisibility();
    });

    api.events.onVariantChange(() => {
        updatePointsEarned();

        // Recharge updates its widget for the new variant before the next paint.
        // Read the settled state in the next frame and sync both hidden inputs.
        requestAnimationFrame(() => {
            tryReplaceRechargeSubscriptionCopy();
            syncRechargeSubscribeCopyVisibility();

            const hasWidget = !!document
                .querySelector('recharge-subscription-widget')
                ?.shadowRoot?.querySelector('.rc-widget__root');

            setDisableRechargeVariantValue(!hasWidget);

            if (!hasWidget) {
                setRechargeOrderValue(false);
            } else {
                const subscriptionRadio = queryShadowDom('input[type="radio"][value="subscription"]');
                setRechargeOrderValue(subscriptionRadio?.checked ?? false);
            }
        });
    });
}

function initRechargeCopyReplacement() {
    tryReplaceRechargeSubscriptionCopy();
    observeRechargeCopyChanges();
    observeWebflowRechargeCopyToggle();
    syncRechargeSubscribeCopyVisibility();
}

// Startup handshake — handles whichever loads first: recharge.js or Recharge widget
if (window.__rechargeWidgetApi) {
    initWithRechargeApi(window.__rechargeWidgetApi);
} else {
    window.__rechargeInitFn = initWithRechargeApi;
}

// Start copy replacement even before the Recharge API callback.
// This helps when the label renders while one-time is selected.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRechargeCopyReplacement);
} else {
    initRechargeCopyReplacement();
}

// Fallback for pages with no Recharge widget, callback never fires
window.addEventListener('load', function() {
    tryReplaceRechargeSubscriptionCopy();
    observeRechargeCopyChanges();
    observeWebflowRechargeCopyToggle();
    syncRechargeSubscribeCopyVisibility();

    setTimeout(function() {
        tryReplaceRechargeSubscriptionCopy();
        syncRechargeSubscribeCopyVisibility();

        if (!window.__rechargeWidgetApi) {
            syncDisableRechargeVariantOnLoad();
            setRechargeOrderValue(false);
            setDisableRechargeVariantValue(true);
        }
    }, 5000);
});